import { useCallback, useEffect, useMemo, useState } from "react"
import { APPROVE_AND_CALL_TOKENS, SupportedTokenName } from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { useInterval } from "usehooks-ts"

import {
  approveToken,
  fetchMats,
  fetchMatsForBorrow,
  RootState,
  selectActiveToken,
  selectDepositActionDailyMats,
  selectDepositActionNewLockDuration,
  selectDepositActionToken,
  selectDepositActionType,
  selectDepositActionUserAmount,
  selectDepositAmount,
  selectHasAccess,
  selectIsConnected,
  selectPortalStatistics,
  selectTokenAllowance,
  selectTokenApprovalStatus,
  selectTokenBalance,
  selectTokenDecimals,
  selectTotalDepositsAmount,
  selectWalletAddress,
  setHasApprovalError,
  setTokenApprovalStatus,
  updateBalances,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { BALANCE_UPDATE_INTERVAL, LOCK_TIMES } from "#/shared/constants"
import {
  calculateDailyMats,
  convertBigNumberShortFormat,
  floatToBigInt,
  formatMats,
  separateThousandsWithComma,
} from "#/shared/utils"

export const usePortal = () => {
  const dispatch = useDappDispatch()
  const address = useDappSelector(selectWalletAddress)
  const hasAccess = useDappSelector(selectHasAccess)

  const fetchPortalDataHandler = useCallback(async () => {
    if (!address || !hasAccess) return

    await dispatch(updateBalances({ fetchOnChainData: false }))
    await dispatch(fetchMatsForBorrow())
    await dispatch(fetchMats())
  }, [address, dispatch, hasAccess])

  // Fetch initial portal data
  useEffect(() => {
    const fetchPortal = async () => {
      fetchPortalDataHandler()
    }

    if (!address || !hasAccess) return

    fetchPortal()
  }, [address, fetchPortalDataHandler, hasAccess])

  // Make balances and deposited amount updated on selected interval (set to 30 seconds)
  useInterval(fetchPortalDataHandler, BALANCE_UPDATE_INTERVAL)
}

export const useTokenApproval = (): {
  approveToken: () => Promise<void>
} => {
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const tokenApprovalStatus = useDappSelector(selectTokenApprovalStatus)

  const dispatch = useDappDispatch()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const tokenAllowance = useDappSelector((state: RootState) =>
    selectTokenAllowance(state, activeToken),
  )

  const amount = useMemo(
    () => floatToBigInt(depositAmount, tokenDecimals),
    [depositAmount, tokenDecimals],
  )

  useEffect(() => {
    // If `approveAndCall` token automatically approve it
    if (APPROVE_AND_CALL_TOKENS.includes(activeToken)) {
      dispatch(setTokenApprovalStatus("approved"))
    } else {
      dispatch(
        setTokenApprovalStatus(
          tokenAllowance >= amount ? "approved" : "non-approved",
        ),
      )
    }
  }, [amount, tokenAllowance, activeToken, dispatch])

  const approveTokenHandler = useCallback(async () => {
    if (tokenApprovalStatus === "approved" || amount <= 0n) return
    dispatch(setTokenApprovalStatus("pending"))

    const { payload: approval } = (await dispatch(
      approveToken({ amount, token: activeToken }),
    )) as PayloadAction<boolean>

    dispatch(setHasApprovalError(!approval))
    dispatch(setTokenApprovalStatus(approval ? "approved" : "non-approved"))
  }, [amount, dispatch, activeToken, tokenApprovalStatus])

  return {
    approveToken: approveTokenHandler,
  }
}

export const useDepositBalance = (token: SupportedTokenName) => {
  const isConnected = useDappSelector(selectIsConnected)

  const tokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, token),
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  return { isConnected, tokenBalance, tokenDecimals }
}

export const useDepositedBefore = () => {
  const [depositedBefore, setDepositedBefore] = useState(false)

  const totalDeposits = useDappSelector(selectTotalDepositsAmount)

  useEffect(() => {
    setDepositedBefore(Number(totalDeposits) !== 0)
  }, [totalDeposits])

  return depositedBefore
}

export const usePortalStatistics = () => {
  const statistics = useDappSelector(selectPortalStatistics)

  const convertedTVL = useMemo(() => {
    if (!statistics) return null

    const { tvl } = statistics

    const { shortFormat, converted } = convertBigNumberShortFormat(tvl)

    return converted ? shortFormat : separateThousandsWithComma(shortFormat, 0)
  }, [statistics])

  const convertedDeposits = useMemo(() => {
    if (!statistics) return null

    const { depositedBitcoin } = statistics

    const { shortFormat, converted } =
      convertBigNumberShortFormat(depositedBitcoin)

    return converted ? shortFormat : separateThousandsWithComma(shortFormat, 0)
  }, [statistics])

  return { convertedTVL, convertedDeposits }
}

export const useLockModalMats = () => {
  const userAmount = useDappSelector(selectDepositActionUserAmount)
  const dailyMats = useDappSelector(selectDepositActionDailyMats)
  const newLockDuration = useDappSelector(selectDepositActionNewLockDuration)
  const lockType = useDappSelector(selectDepositActionType)
  const token = useDappSelector(selectDepositActionToken)

  const newDailyMats = useMemo(() => {
    if (!token) return 0

    const lock = LOCK_TIMES.find(
      ({ period }) => period === Math.round(newLockDuration),
    )

    const multiplier = lock?.multiplier ?? 0

    const calculatedDailyMats = calculateDailyMats(
      userAmount,
      multiplier,
      token,
    )

    return formatMats(calculatedDailyMats)
  }, [userAmount, newLockDuration, token])

  const adjustedDailyMats = lockType === "extend" ? dailyMats : 0

  return { dailyMats: adjustedDailyMats, newDailyMats }
}
